import { SB_COLORS } from "theme/SbThemeV2";

// ─── UI text ──────────────────────────────────────────────────────────────────

export const TEXT = {
  // Page heading
  PAGE_TITLE_PREFIX: "Skills Bridger - Inferred skills:",
  VIEW_TITLE_JD: "Required in current role",
  VIEW_TITLE_ADDITIONAL: "Additional skills",

  // Description paragraph
  DESCRIPTION:
    "Skills Bridger infers your current and required skills using information provided by you and your organization to recommend learning paths, roles, gigs and shadowing opportunities. For accuracy, review and upload missing source files when you start using this module, and thereafter at least once a quarter. HR may periodically review your files and hiring managers will review them if you apply for a role.",

  // Progress circle card
  SCORE_CARD_TITLE: "Your SkillsBridge Readiness Index",
  SCORE_CARD_SUBTITLE: "(Required vs Current Proficiency)",

  // Action row labels
  SOURCES_LABEL: "View and upload your source files here.",
  LINKEDIN_LABEL: "Connect Your LinkedIn Profile:",
  VIEW_JD_LABEL: "View Job Description:",
  CLOSE_JD_LABEL: "Close Job Description:",

  // Buttons
  BTN_SOURCE_FILES: "Source Files",
  BTN_CONNECT_LINKEDIN: "Connect LinkedIn",
  BTN_VIEW_JD: "View JD",
  BTN_CLOSE_JD: "Close JD",
  BTN_VIEW_DETAILS: "View Details",
  BTN_HIDE_DETAILS: "Hide Details",
  BTN_TAKE_TOUR: "Take the Tour",

  // Job description modal
  JD_MODAL_TITLE: "Job Description",
  JD_MODAL_EMPTY: "No job description is available.",

  // Proficiency scale footer
  SCALE_HEADING: "Skills Proficiency Scale",
  SCALE_SEPARATOR: " : ",

  // Pagination
  PAGINATION_SHOWING: "Showing",
  PAGINATION_ITEMS_PER_PAGE: "Items per page",

  // Success alert
  SUCCESS_ALERT_TITLE: "Self-reported proficiency updated successfully",

  // Equal proficiency warning modal
  EQUAL_PROFICIENCY_MODAL_TITLE: "Value matches current proficiency",
  EQUAL_PROFICIENCY_MODAL_CONTENT: "Self-reported proficiency matches current proficiency. Please provide a different value if necessary.",
  EQUAL_PROFICIENCY_MODAL_OK: "OK",

  // Self-reported proficiency cell
  INVALID_PROFICIENCY_MSG: "Please enter a value between 0 and 5",

  // Inferred proficiency column header
  INFERRED_PROFICIENCY_TITLE: "Inferred Proficiency",
  PROFICIENCY_SCALE_RANGE: "(0-5 Scale)",
  CURRENT_PROFICIENCY_LABEL: "Current Proficiency",
  REQUIRED_PROFICIENCY_LABEL: "Required Proficiency",

  // Proficiency popover
  POPOVER_QUESTION:
    "What should you do if inferred proficiency does not match your expectations?",
  POPOVER_BODY:
    "This may happen if Skills Bridger lacks enough evidence to assess your skills. " +
    "In such cases, enter your self-reported proficiency as you believe it should be, " +
    "along with an explanation to help improve the algorithm. After reviewing all " +
    "skills, click the Source files tab to upload missing files, which will help " +
    "Skills Bridger re-infer your skills. Consider connecting your LinkedIn profile " +
    "to improve accuracy. You can upload more evidence anytime to support your " +
    "proficiency. Keeping your records current ensures a more accurate assessment.",

  // Reason modal
  REASON_MODAL_TITLE:
    "Mention reasons why you expect a higher or lower skills proficiency (this helps improve the algorithm)",
  REASON_MODAL_PLACEHOLDER:
    "Include the reasons you expect a skill to be rated higher or lower in proficiency. " +
    "After reviewing all skills, if anything is missing or outdated, please upload supporting documents via the Source files tab in the top-right corner. " +
    "This helps Skills Bridger reassess your skills and ensures your Skills Readiness Index is up to date",
  REASON_MODAL_OK: "Submit",
  REASON_MODAL_CANCEL: "Cancel",
};

// ─── Brand colors ─────────────────────────────────────────────────────────────

export const TEAL = SB_COLORS.primary;
export const CURRENT_STROKE = "#007bff";
export const REQUIRED_STROKE = "#87ceeb";
export const DETAIL_HEADER_BG = "#e6f7f6";

// ─── View modes ───────────────────────────────────────────────────────────────

export const VIEW_MODE_JD = "jd_skills";
export const VIEW_MODE_ADDITIONAL = "additional_skills";

// ─── Proficiency scale ────────────────────────────────────────────────────────

export const SKILL_LEVEL = 3;
export const MIN_PROFICIENCY = 0;
export const MAX_PROFICIENCY = 5;

// ─── Pagination ───────────────────────────────────────────────────────────────

export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = ["10", "20", "30"];

// ─── Timers ───────────────────────────────────────────────────────────────────

export const SUCCESS_TIMEOUT_MS = 3000;
export const EQUAL_ALERT_TIMEOUT_MS = 5000;

// ─── Reason modal ─────────────────────────────────────────────────────────────

export const REASON_MODAL_WIDTH = 560;
export const REASON_TEXTAREA_ROWS = 6;

// ─── Scale details modal ──────────────────────────────────────────────────────

export const SCALE_DETAILS_MODAL_WIDTH = 820;

// ─── Alert types ─────────────────────────────────────────────────────────────

export const ALERT_TYPE_SUCCESS = "success";

// ─── Proficiency bar ──────────────────────────────────────────────────────────

export const BAR_HEIGHT = 22;
export const MIN_LABEL_PCT = 10;

// ─── Columns ──────────────────────────────────────────────────────────────────

export const COL_TITLE = {
  SKILL_NAME:             "Skill Name",
  SELF_REPORTED:          "Self-Reported Proficiency",
  SELF_REPORTED_SUBTITLE: "(Only if different from current)",
};

export const COL_WIDTH = {
  SKILL_NAME:      "40%",
  INFERRED:        "28%",
  SELF_REPORTED:   "20%",
};

export const CONTEXTUAL_NOTES_FONT_SIZE = 14;
export const SELF_REPORTED_SUBTITLE_FONT_SIZE = 12;

// ─── Guided tour ──────────────────────────────────────────────────────────────
// Steps, order and text taken verbatim from the tour spec sheet (skipping the
// struck-out rows: Connect LinkedIn, Download JD, Function Average, Priority
// Skill). `title` = spec "Element Name", `description` = spec "Message to User",
// `placement` = spec "Tooltip Position". `key` maps a step to its target ref in
// useEmployeeSkillsBridger. Steps with no `key` (Welcome / Finish) have no
// target, so AntD renders them as a centered card.

// This module's key inside the shared per-user "tour seen" object
// (see uiCore/SbTour/tourSeen).
export const TOUR_MODULE_KEY = "employee-skills-bridger";

export const TOUR_STEPS = [
  {
    // Welcome — centered greeting (spec step 0)
    title: "Welcome to Skills Bridger",
    description:
      "Your personal skills intelligence hub. Let's take a quick 2-minute tour and get your SkillsBridge Readiness Index moving.",
    primaryText: "Start the Tour",
    secondaryText: "I'll explore on my own",
  },
  {
    // On "Next" from this step the tour hands off to the in-drawer tour: it opens
    // the Source Files drawer, walks Upload → Save and Infer → Agree & Submit,
    // then resumes the main tour at the next step. See changeTourStep +
    // handleDrawerTourComplete in useEmployeeSkillsBridger.
    key: "sourceFiles",
    handoffToDrawer: true,
    title: "Source Files Button",
    description:
      "Please add as much information as you can in this section. Your company may already have added a few documents, but you can upload more as needed.\n\n" +
      "Include any documents that help show your current skills, as well as additional skills you want to highlight beyond your current role. For example: your resume, performance reviews, goals and objectives, LinkedIn profile, certificates, and other relevant documents.\n\n" +
      "Upload anything useful from as far back as possible. Skills Bridger will infer your proficiency from the documents you provide, so the more context you add, the more accurate the match will be for learning courses and open roles.",
    placement: "bottom",
  },
  {
    key: "gauge",
    title: "SkillsBridge Readiness Index",
    description:
      "Your SkillsBridge Readiness Index measures how closely your current skill levels match what's required for your role. Every document you upload and skill gap you close moves this number forward.",
    placement: "left",
  },
  {
    key: "table",
    title: "Skills Table — Inferred Proficiency",
    description:
      "This table shows your inferred skills. The blue bar is your current proficiency. The blue triangle ▲ shows what's required for your role. Where the bar falls short of the triangle - that's a skill gap.",
    placement: "top",
  },
  {
    key: "selfReported",
    title: "Self-Reported Proficiency Column",
    description:
      "If you think an inferred score is inaccurate, upload supporting evidence in the Source Files tab. Before uploading additional evidence, please review our skills proficiency scale at the bottom of the screen.",
    placement: "left",
  },
  {
    key: "scale",
    title: "Skills Proficiency Scale & View Details",
    description: "Click View Details to see the full definition for each level.",
    placement: "top",
  },
  {
    key: "toggle",
    title: "Role vs. Additional Skills Toggle",
    description:
      "Toggle between two views: Required in current role shows skills your role demands vs. what you have today. Additional skills shows skills you've built beyond your role — great for exploring stretch opportunities.",
    placement: "bottom",
  },
  {
    // Finish — centered greeting (spec step 11). Primary routes to Source Files.
    title: "You're all set!",
    description:
      "Start by uploading your Source Files to reveal your skills and get your SkillsBridge Readiness Index moving.",
    primaryText: "Upload Source Files",
    secondaryText: "Done",
  },
];

// ─── Skill ranking ──────────────────────────────────────────────────────────
// Ranks skills by max(task_importance × skill_importance) across all tasks,
// matching the bubble-chart ordering. Skills not tied to any task rank last (0).
export const sortSkillsByImportance = (skills = [], tasks = []) => {
  const importanceBySkill = tasks.reduce((acc, task) => {
    const taskImportance = task.task_importance ?? 1;
    (task.skills ?? []).forEach(({ skill, importance }) => {
      acc[skill] = Math.max(acc[skill] ?? 0, taskImportance * (importance ?? 1));
    });
    return acc;
  }, {});
  return [...skills].sort(
    (a, b) => (importanceBySkill[b.skill] ?? 0) - (importanceBySkill[a.skill] ?? 0)
  );
};
